<template>
  <div class="dashboard-layout h-screen flex">
    <Sidebar :collapsed="sidebarCollapsed" />
    
    <div class="flex-1 flex flex-col overflow-hidden">
      <Header 
        title="Products" 
        :showSearch="false" 
        :showMenuToggle="true" 
        @toggle-sidebar="toggleSidebar"
      />
      
      <!-- Main Content -->
      <main class="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-6 transition-all duration-300">
        <LoadingSpinner :loading="loading" message="Loading products..." />
        <Toast ref="toast" />

        <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span class="block sm:inline">{{ error }}</span>
        </div>

        <div v-if="!loading && !error">
          <!-- Summary Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="bg-primary-600 text-white rounded-lg p-6">
              <h3 class="text-lg font-semibold mb-2">Total Products</h3>
              <p class="text-3xl font-bold">{{ products.length }}</p>
            </div>
            <div class="bg-secondary-600 text-white rounded-lg p-6">
              <h3 class="text-lg font-semibold mb-2">Active Products</h3>
              <p class="text-3xl font-bold">{{ activeProducts.length }}</p>
            </div>
            <div class="bg-success-600 text-white rounded-lg p-6">
              <h3 class="text-lg font-semibold mb-2">Inactive Products</h3>
              <p class="text-3xl font-bold">{{ inactiveProducts.length }}</p>
            </div>
          </div>

          <!-- Products Table -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Code</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Updated</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="product in products" :key="product.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ product.code }}
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <div class="text-sm text-gray-900 dark:text-white">{{ product.name }}</div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">{{ product.description }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                      product.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    ]">
                      {{ product.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(product.created_at) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(product.updated_at) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button @click="openEditModal(product)" class="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300">
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  </div>

  <!-- Edit Product Modal -->
  <div v-if="showEditModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
    <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
      <div class="mt-3">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit Product</h3>
        <form @submit.prevent="updateProduct" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Code</label>
            <input
              v-model="editingProduct.code"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input
              v-model="editingProduct.name"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              v-model="editingProduct.description"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows="3"
            ></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <select
              v-model="editingProduct.is_active"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            >
              <option :value="true">Active</option>
              <option :value="false">Inactive</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3 mt-4">
            <button
              type="button"
              @click="closeEditModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              :disabled="loading"
            >
              {{ loading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { useAuthStore } from '../stores/auth';
import apiService from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import Toast from '../components/Toast.vue';
import Header from '../components/Header.vue';
import Sidebar from '../components/Sidebar.vue';

const authStore = useAuthStore();
const products = ref([]);
const loading = ref(true);
const error = ref(null);
const showEditModal = ref(false);
const editingProduct = ref(null);
const toast = inject('toast');
const sidebarCollapsed = ref(false);

const activeProducts = computed(() => products.value.filter(product => product.is_active));
const inactiveProducts = computed(() => products.value.filter(product => !product.is_active));

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const fetchProducts = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await apiService.get('/api/products', {
      headers: { Authorization: `Bearer ${authStore.token}` }
    });
    products.value = response.data.products;
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to fetch products';
    toast.value?.addToast(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

const openEditModal = (product) => {
  editingProduct.value = { ...product };
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingProduct.value = null;
};

const updateProduct = async () => {
  try {
    loading.value = true;
    const response = await apiService.put(`/api/products/${editingProduct.value.id}`, editingProduct.value, {
      headers: { Authorization: `Bearer ${authStore.token}` }
    });
    
    // Update the product in the local products array
    const index = products.value.findIndex(p => p.id === editingProduct.value.id);
    if (index !== -1) {
      products.value[index] = response.data.product;
    }
    
    closeEditModal();
    toast.value?.addToast('Product updated successfully', 'success');
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to update product';
    toast.value?.addToast(error.value, 'error');
    console.error('Error updating product:', err);
  } finally {
    loading.value = false;
  }
};

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

onMounted(async () => {
  await fetchProducts();
});
</script> 